import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { VisitAddFacade } from '@beautyapp/shared';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpaceModule } from 'ng-zorro-antd/space';

import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { AddBookingComponent } from '../add-booking/add-booking.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BookingDisableReasonComponent } from '../../../components/booking-disable-reason/booking-disable-reason.component';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  NzDrawerModule,
  NzDrawerRef,
  NzDrawerService,
} from 'ng-zorro-antd/drawer';
import { ChangeScheduleComponent } from '../change-schedule/change-schedule.component';
import { ChooseAgreementComponent } from '../choose-agreement/choose-agreement.component';
import { rxResource } from '@angular/core/rxjs-interop';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { tap } from 'rxjs/operators';
import { AgreementViewComponent } from '../agreement-view/agreement-view.component';
import { format } from 'date-fns';
import { AddBookingServiceComponent } from '../add-booking-service/add-booking-service.component';
import { BookingServiceService, VisitCommentService } from '@beautyapp/core';
import {
  NzPopconfirmDirective,
  NzPopconfirmModule,
} from 'ng-zorro-antd/popconfirm';
import { EditBookingInfoComponent } from '../edit-booking-info/edit-booking-info.component';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { catchError, of, throwError } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { VisitDisableReasonComponent } from '../../../components/visit-disable-reason/visit-disable-reason.component';
import { NgxPermissionsModule } from 'ngx-permissions';
import { BookingAuditHistoryComponent } from '../../../booking-modal/components/booking-audit-history/booking-audit-history.component';
import { DatePipe } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'beautyapp-booking-list',
  imports: [
    NzCardModule,
    NzGridModule,
    NzDividerModule,
    NzTagModule,
    NzIconModule,
    NzSpaceModule,
    NzAlertModule,
    NzButtonModule,
    NzModalModule,
    NzDrawerModule,
    NzSpinComponent,
    NzPopconfirmModule,
    NzTooltipDirective,
    NgxPermissionsModule,
    TranslatePipe,
    FormsModule,
    NzInputModule,
    DatePipe,
  ],
  templateUrl: './booking-list.component.html',
  styleUrl: './booking-list.component.scss',
})
export class BookingListComponent {
  visitAddFacade = inject(VisitAddFacade);
  modalService = inject(NzModalService);
  drawerService = inject(NzDrawerService);
  translateService = inject(TranslateService);
  notificationService = inject(NzNotificationService);
  bookingServiceService = inject(BookingServiceService);
  visitCommentService = inject(VisitCommentService);

  drawerRef = inject(NzDrawerRef);

  visitId = input.required<number>();
  reloadCount = input<number>();

  getVisit = output<number>();

  constructor() {
    effect(() => {
      if (this.reloadCount()) {
        this.bookingResource.reload();
      }
    });
  }

  bookingResource = rxResource({
    params: () => ({ visitId: this.visitId() }),
    stream: ({ params }) =>
      this.visitAddFacade
        .getBookings(params.visitId)
        .pipe(catchError(() => of([]))),
  });

  bookings = computed(() => this.bookingResource.value() || []);

  /**
   * Gets the latest endTimeDate from the provided bookings array
   * @param bookings The array of booking objects
   * @returns The latest endTimeDate as a Date object
   */
  getLatestEndTime(bookings: { endTimeDate: string }[]): Date {
    if (!bookings || bookings.length === 0) {
      return new Date();
    }

    // Find the latest endTimeDate using reduce
    const latestBooking = bookings.reduce((latest, current) => {
      const currentDate = new Date(current.endTimeDate);
      return currentDate > latest ? currentDate : latest;
    }, new Date(0)); // Initialize with the earliest possible date

    return latestBooking;
  }

  /**
   * Checks if booking has invalid time (startTime > endTime)
   */
  isInvalidTimeRange(booking: any): boolean {
    if (!booking.startTimeDate || !booking.endTimeDate) return false;
    const start = new Date(booking.startTimeDate);
    const end = new Date(booking.endTimeDate);
    return start.getTime() >= end.getTime();
  }

  addBooking() {
    const getLatestEndTime = this.getLatestEndTime(this.bookings());

    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          visitId: this.visitId(),
          employee: this.visitAddFacade.employee(),
          employeeId: this.visitAddFacade.employeeId,
          // Adding to a visit that already exists: always a follow-up booking,
          // so the employee is pre-selected but changeable (see
          // isEmployeePickerLocked).
          followUpBooking: true,
          userId: this.visitAddFacade.user()?.id,
          bookingDate: this.visitAddFacade.bookingDate,
          startTime: format(getLatestEndTime, 'yyyy-MM-dd HH:mm'),
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      });
  }

  addBookingService(booking: any) {
    console.log(booking);
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingServiceComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          booking,
          bookingDate: this.visitAddFacade.bookingDate,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      });
  }

  editBookingService(booking: any, bookingService: any) {
    console.log(booking);
    console.log(bookingService);
    this.modalService
      .create({
        nzClosable: true,
        nzMaskClosable: false,
        nzTitle: this.translateService.instant('calendar.planVisit'),
        nzContent: AddBookingServiceComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzFooter: null,
        nzData: {
          bookingService,
          employeeId: booking.employee.id,
          bookingId: booking.id,
          bookingDate: this.visitAddFacade.bookingDate,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      });
  }

  viewBookingService(booking: any, bookingService: any) {
    this.modalService.create({
      nzClosable: true,
      nzMaskClosable: false,
      nzTitle: this.translateService.instant('calendar.planVisit'),
      nzContent: AddBookingServiceComponent,
      nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
      nzFooter: null,
      nzData: {
        bookingService,
        employeeId: booking.employee.id,
        bookingId: booking.id,
        viewOnly: true,
      },
    });
  }

  editBooking(booking: any) {
    this.drawerService
      .create({
        nzContent: ChangeScheduleComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzData: {
          bookings: this.bookings(),
          visitId: this.visitId(),
          visitDate: this.visitAddFacade.visit()?.bookingDate,
          focusBookingId: booking.id,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) {
          this.getVisit.emit(this.visitId());
          this.bookingResource.reload();
        }
      });
  }

  deleteBooking(bookingId: string) {
    const bookingCount = this.bookings().length;

    if (bookingCount === 1) {
      this.visitDisableReason();
      return;
    }

    this.modalService.create({
      nzTitle: 'პროცედურის გაუქმება',
      nzContent: BookingDisableReasonComponent,
      nzWidth: '600px',
      nzStyle: { top: '0px' },
      nzData: {
        bookingId: bookingId,
      },
      nzOnOk: () => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      },
    });
  }

  deleteBookingService(booking: any) {
    this.bookingServiceService
      .delete(booking.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      });
  }

  visitDisableReason() {
    this.modalService.create({
      nzTitle: 'ვიზიტის გაუქმება',
      nzContent: VisitDisableReasonComponent,
      nzData: {
        visitId: this.visitAddFacade.visitId,
      },
      nzOnOk: () => {
        this.drawerRef.close();
      },
    });
  }

  changeSchedule() {
    this.drawerService
      .create({
        nzContent: ChangeScheduleComponent,
        nzWidth: window.innerWidth <= 1024 ? '100%' : '70%',
        nzData: {
          bookings: this.bookings(),
          visitId: this.visitId(),
          visitDate: this.visitAddFacade.visit()?.bookingDate,
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) {
          this.getVisit.emit(this.visitId());
          this.bookingResource.reload();
        }
      });
  }

  agreement(bookingId: string, serviceId: string) {
    console.log(bookingId);
    this.modalService
      .create({
        nzTitle: 'თანხმობა',
        nzContent: ChooseAgreementComponent,
        nzWidth: '600px',
        nzStyle: { top: '0px' },
        nzData: {
          bookingId: bookingId,
          serviceId: serviceId,
          userId: this.visitAddFacade.user()?.id,
        },
        nzOnOk: () => {
          this.getVisit.emit(this.visitId());
          this.bookingResource.reload();
        },
      })
      .afterClose.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.getVisit.emit(this.visitId());
        this.bookingResource.reload();
      });
  }

  agreementView(userAgreements: any[]) {
    if (userAgreements.length === 0) {
      return;
    }
    const modal = this.modalService.create({
      nzTitle: 'თანხმობის დეტალები',
      nzContent: AgreementViewComponent,
      nzWidth: '600px',
      nzFooter: null,
    });

    modal.componentInstance?.userAgreements.set(userAgreements);
  }

  viewBookingHistory(bookingId: string) {
    this.drawerService.create({
      nzTitle: 'ჯავშნის ისტორია',
      nzContent: BookingAuditHistoryComponent,
      nzWidth: window.innerWidth <= 1024 ? '100%' : '600px',
      nzData: {
        bookingId: bookingId,
      },
    });
  }

  // Fast comment
  fastCommentBookingId = signal<string | null>(null);
  fastCommentText = '';
  fastCommentSubmitting = signal(false);

  toggleFastComment(bookingId: string): void {
    if (this.fastCommentBookingId() === bookingId) {
      this.fastCommentBookingId.set(null);
      this.fastCommentText = '';
    } else {
      this.fastCommentBookingId.set(bookingId);
      this.fastCommentText = '';
    }
  }

  submitFastComment(bookingId: string): void {
    if (!this.fastCommentText.trim()) return;

    this.fastCommentSubmitting.set(true);
    this.visitCommentService
      .createComment(this.visitId(), {
        text: this.fastCommentText.trim(),
        bookingId,
      })
      .subscribe({
        next: () => {
          this.fastCommentText = '';
          this.fastCommentBookingId.set(null);
          this.fastCommentSubmitting.set(false);
          this.bookingResource.reload();
        },
        error: () => {
          this.fastCommentSubmitting.set(false);
        },
      });
  }
}
